import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useGroup } from '../lib/places';
import PlaceMap, { type Bounds } from '../components/PlaceMap';
import PlaceCard from '../components/PlaceCard';
import { DaySelect, GroupChips, ScoreSlider, TagToggles } from '../components/Filters';
import { Empty, ErrorNote, PageHeader, Spinner } from '../components/ui';

const MAP_LIMIT = 500;

export default function ExplorePage() {
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [group, setGroup] = useGroup();
  const [tags, setTags] = useState<string[]>([]);
  const [minScore, setMinScore] = useState(0);
  const [day, setDay] = useState<number | ''>('');
  const [bounds, setBounds] = useState<Bounds | null>(null);
  const [zoom, setZoom] = useState(9);
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => window.clearTimeout(t);
  }, [q]);

  // Round the viewport so tiny pans don't refetch.
  const roundedBounds = useMemo<Bounds | undefined>(
    () => (bounds ? (bounds.map((n) => Math.round(n * 50) / 50) as Bounds) : undefined),
    [bounds],
  );

  const places = useQuery({
    queryKey: ['explore', debouncedQ, group, tags, minScore, day, roundedBounds],
    queryFn: () =>
      api.searchPlaces({
        q: debouncedQ || undefined,
        group,
        tags,
        minScore,
        openOnDay: day === '' ? undefined : day,
        bounds: debouncedQ ? undefined : roundedBounds,
        sort: 'score',
        pageSize: MAP_LIMIT,
      }),
    enabled: !!roundedBounds || !!debouncedQ,
    placeholderData: keepPreviousData,
  });

  const items = places.data?.items ?? [];
  const total = places.data?.totalCount ?? 0;
  const what = group === 'bars' ? 'bars' : group === 'bites' ? 'bites' : 'places';

  return (
    <>
      <PageHeader eyebrow="The map" title="Explore">
        Pan and zoom to look around. Mugs are bars, a mug and a burger is a bar with food, and burgers, cups and cones are bites. Brighter pins score higher for backroad
        character, and the boldest ones are hand-picked.
      </PageHeader>

      <div className="mx-auto max-w-7xl px-4 pb-14 sm:px-6">
        <div className="card mb-5 grid gap-4 p-4 md:grid-cols-[1fr_1fr_200px]">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="q">Search by name or town</label>
            <input id="q" type="search" placeholder="Try “tavern”, “diner”, “perch” or “Irons”" value={q} onChange={(e) => setQ(e.target.value)} />
            <span className="text-[13px] text-dim">Searching looks everywhere, not just the map view.</span>
          </div>
          <ScoreSlider value={minScore} onChange={setMinScore} />
          <DaySelect value={day} onChange={setDay} />
          <div className="flex flex-wrap items-center gap-3 md:col-span-3">
            <GroupChips value={group} onChange={setGroup} />
            <TagToggles value={tags} onChange={setTags} />
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1fr_420px]">
          <div className="flex flex-col gap-2">
            <PlaceMap
              places={items}
              selectedId={selected}
              onSelect={setSelected}
              onBoundsChange={(b, z) => {
                setBounds(b);
                setZoom(z);
              }}
              fitPoints={debouncedQ ? items.map((b) => [b.lat, b.lng]) : undefined}
              fitKey={debouncedQ ? `${debouncedQ}-${items.length}` : undefined}
              className="h-[460px] lg:sticky lg:top-24 lg:h-[680px]"
              label="Map of bars and bites in view"
            />
            <p className="text-sm text-muted tabular-nums" aria-live="polite">
              {places.isFetching
                ? 'Loading…'
                : total > items.length
                  ? `Showing the top ${items.length.toLocaleString()} of ${total.toLocaleString()} ${what} here. Zoom in for more.`
                  : `${total.toLocaleString()} ${what} ${debouncedQ ? 'match' : 'in view'}.`}
              {zoom < 7 && !debouncedQ && ` Zoomed way out, showing the highest scoring ${what} only.`}
            </p>
          </div>

          <div className="flex flex-col gap-3">
            {places.isLoading && <Spinner label="Finding places" />}
            {places.error && <ErrorNote error={places.error} />}
            {!places.isLoading && items.length === 0 && (
              <Empty>No {what} here with those filters. Try zooming out or lowering the backroad score.</Empty>
            )}
            {items.slice(0, 60).map((b) => (
              <PlaceCard
                key={b.id}
                place={b}
                day={day === '' ? undefined : day}
                selected={b.id === selected}
                onSelect={() => setSelected(b.id)}
                compact
              />
            ))}
            {items.length > 60 && <p className="text-sm text-muted">Top 60 listed. Zoom in or add filters to narrow it down.</p>}
          </div>
        </div>
      </div>
    </>
  );
}
